/**
 * UpdateNotification — in-app toast for the auto-updater (Phase 18).
 *
 * Subscribes to `window.electronAPI.updater.onStatus` and renders a
 * floating toast in the bottom-right corner when something interesting
 * happens. Stays quiet during routine background checks; only surfaces:
 *
 *   - 'downloading' → small toast with progress bar (transient)
 *   - 'downloaded'  → persistent toast with "Restart now" button
 *   - 'error'       → small toast with error message (auto-dismiss after 10s)
 *
 * Quiet states ('checking', 'up-to-date', 'dev', 'unavailable') don't
 * render anything — those are only for the Settings panel's manual check.
 *
 * Single-instance design: one toast at a time, latest status wins. The
 * "downloaded" state is sticky (no auto-dismiss) until the user clicks
 * Restart or Later.
 *
 * Renders nothing in non-Electron contexts (web preview / tests) — guards
 * on the IPC bridge's existence.
 */

import { useEffect, useState } from 'react';
import { Download, RefreshCw, X, AlertCircle, CheckCircle2 } from 'lucide-react';

const isAvailable = () =>
  typeof window !== 'undefined' &&
  typeof window.electronAPI?.updater?.onStatus === 'function';

const fmtBytes = (n) => {
  if (!n || !Number.isFinite(n)) return '';
  if (n > 1e6) return `${(n / 1e6).toFixed(1)} MB/s`;
  if (n > 1e3) return `${(n / 1e3).toFixed(0)} KB/s`;
  return `${n} B/s`;
};

const UpdateNotification = ({ darkMode = true }) => {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    if (!isAvailable()) return undefined;
    const unsubscribe = window.electronAPI.updater.onStatus((payload) => {
      setStatus(payload);
      // Re-arm visibility whenever a meaningful new state arrives — the
      // user shouldn't have to dismiss the toast separately for every
      // future event. Errors and downloads count as meaningful.
      if (payload?.status === 'downloaded' || payload?.status === 'downloading' || payload?.status === 'error') {
        setDismissed(false);
      }
    });
    return unsubscribe;
  }, []);

  // Auto-dismiss errors after 10s so a flaky check doesn't leave a red
  // toast on screen forever. Downloads + downloaded stay until the user
  // interacts.
  useEffect(() => {
    if (status?.status !== 'error') return undefined;
    const t = setTimeout(() => setDismissed(true), 10_000);
    return () => clearTimeout(t);
  }, [status]);

  if (!status || dismissed) return null;

  const handleRestart = async () => {
    setRestarting(true);
    try {
      await window.electronAPI.updater.restartAndInstall();
      // The app will close + relaunch — no further UI updates needed.
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[UpdateNotification] restart failed:', e?.message || e);
      setRestarting(false);
    }
  };

  const baseToast = `fixed bottom-4 right-4 z-50 max-w-sm rounded-xl shadow-2xl border backdrop-blur-md
    transition-[background-color,border-color,box-shadow,color] duration-150`;
  const darkBox = 'bg-gray-900/95 border-white/10 text-white';
  const lightBox = 'bg-white border-gray-200 text-gray-900';
  const wrapCls = `${baseToast} ${darkMode ? darkBox : lightBox}`;

  // ── downloaded: persistent "Restart to install" toast ────────────────
  if (status.status === 'downloaded') {
    return (
      <div className={wrapCls} role="alert" aria-live="polite">
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className={`shrink-0 p-2 rounded-lg ${darkMode ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
              <CheckCircle2 size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">Update ready to install</div>
              <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Version {status.latestVersion || 'newer'} is downloaded. Restart Journifi to apply it.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss for now"
              className={`shrink-0 p-1 rounded-md ${darkMode ? 'text-gray-500 hover:text-gray-200 hover:bg-white/5' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
            >
              <X size={14} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRestart}
              disabled={restarting}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-wait ${
                darkMode
                  ? 'bg-emerald-500/25 border-emerald-400/50 text-emerald-50 ring-1 ring-inset ring-emerald-400/25 hover:bg-emerald-500/35 hover:border-emerald-400/70 hover:shadow-[0_0_16px_rgba(16,185,129,0.3)]'
                  : 'bg-emerald-100 border-emerald-300 text-emerald-800 hover:bg-emerald-200 hover:border-emerald-400'
              }`}
            >
              <RefreshCw size={13} className={restarting ? 'animate-spin' : ''} />
              {restarting ? 'Restarting…' : 'Restart now'}
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                darkMode
                  ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20 hover:text-white'
                  : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300 hover:text-gray-900'
              }`}
            >
              Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── downloading: small progress toast ────────────────────────────────
  if (status.status === 'downloading') {
    const pct = Math.max(0, Math.min(100, Math.round(status.percent || 0)));
    return (
      <div className={wrapCls}>
        <div className="p-3 space-y-2 min-w-[260px]">
          <div className="flex items-center gap-2.5">
            <div className={`shrink-0 p-1.5 rounded-md ${darkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
              <Download size={14} className="animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold">Downloading update {status.latestVersion ? `v${status.latestVersion}` : ''}</div>
              <div className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {pct}%{status.bytesPerSecond ? ` · ${fmtBytes(status.bytesPerSecond)}` : ''}
              </div>
            </div>
          </div>
          <div className={`h-1 w-full rounded-full overflow-hidden ${darkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
            <div
              className={`h-full transition-[background-color,border-color,box-shadow,color] duration-150 ${darkMode ? 'bg-blue-400' : 'bg-blue-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── error: small auto-dismissing toast ───────────────────────────────
  if (status.status === 'error') {
    return (
      <div className={wrapCls}>
        <div className="p-3 flex items-start gap-2.5 max-w-[320px]">
          <div className={`shrink-0 p-1.5 rounded-md ${darkMode ? 'bg-red-500/15 text-red-300' : 'bg-red-100 text-red-700'}`}>
            <AlertCircle size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold">Update check failed</div>
            <div className={`text-[10px] mt-0.5 break-words ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {status.error || 'Unknown error'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className={`shrink-0 p-1 rounded-md ${darkMode ? 'text-gray-500 hover:text-gray-200 hover:bg-white/5' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
          >
            <X size={12} />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default UpdateNotification;
